interface TakaLogoProps {
  className?: string
  /**
   * Surface the logo sits on. Only affects the wordmark color:
   * - "dark" (default) — wordmark renders in paper.
   * - "light"          — wordmark renders in ink.
   * The "T" mark + dark chip are fixed by brand rule and cannot be overridden.
   * See playbook/design-system/logo.md.
   */
  surface?: 'dark' | 'light'
}

// Hex literals (not CSS vars) because inline SVG can't read `var(--…)` without
// extra plumbing. Each constant tracks a T3 token in `styles/design-system.css`
// and must be updated together. The static SVG exports in `/brand/*.svg`
// (and the marketing twin in `apps/website/app/components/TakaLogo.tsx`)
// share these values — keep all five in sync.
const CHIP_FILL = '#0a0c00' // --ink-on-acid (the dark ink that pairs with acid)
const T_FILL = '#C6FF3D' // --acid (canonical brand action color)
const WORDMARK_PAPER = '#f5f7fa' // --paper (dark-theme body text)
const WORDMARK_INK = '#101828' // --ink (light-theme body text)

// Geometry shared with the static SVG files at:
//   apps/client/public/brand/taka-logo.svg
//   apps/website/public/brand/taka-logo.svg
//   apps/website/public/taka-logo.svg
//   playbook/marketing/brand/taka-logo.svg
// All four use chip rect (-7.75,-6,44,44) + wordmark translate(12,0). Keeping
// React aligned to those means a designer downloading the SVG gets exactly the
// chrome they see in the app, and the baked PNG exports stay accurate.
// Light surface adds 4 extra units of wordmark gap — the only spacing
// difference between surfaces, kept tight on dark, slightly roomier on light.
const VIEWBOX = {
  dark: { wordmarkOffset: 12, viewBoxWidth: 122 },
  light: { wordmarkOffset: 16, viewBoxWidth: 126 },
} as const

/**
 * Full Taka lockup: chip-T + wordmark.
 *
 * The acid "T" always sits on its near-black rounded chip. Only the wordmark
 * adapts to the surface (paper on dark, ink on light). This enforces the
 * chip-on-T invariant from playbook/design-system/logo.md — callers cannot
 * recolor the icon.
 *
 * For square-container slots (favicons, OAuth popups, avatar tiles) use the
 * static asset at `assets/taka-logo-icon.svg` instead.
 *
 * Marketing-site twin lives at `apps/website/app/components/TakaLogo.tsx`.
 * Keep both in sync if the artwork changes.
 */
export const TakaLogo = ({ className = 'h-7 w-auto -my-1', surface = 'dark' }: TakaLogoProps) => {
  const wordmarkFill = surface === 'light' ? WORDMARK_INK : WORDMARK_PAPER
  const { wordmarkOffset, viewBoxWidth } = VIEWBOX[surface]
  return (
    <svg className={className} viewBox={`-8 -6 ${viewBoxWidth} 44`} aria-label="Taka">
      <rect x="-7.75" y="-6" width="44" height="44" rx="8" fill={CHIP_FILL} />
      <g transform="translate(0.5 0)">
        <path
          d="M17.1948 31.0919C17.1948 31.6123 16.773 32.0341 16.2526 32.0341H7.30189C6.78154 32.0341 6.35971 31.6123 6.35971 31.0919V16.2526C6.35971 15.7323 5.93789 15.3104 5.41753 15.3104H0.942181C0.421829 15.3104 0 14.8886 0 14.3682V5.65308C0 5.13273 0.421828 4.7109 0.94218 4.7109H4.7109V9.65734C4.7109 10.1777 5.13273 10.5995 5.65308 10.5995H10.1284C10.6488 10.5995 11.0706 11.0214 11.0706 11.5417V26.381C11.0706 26.9014 11.4924 27.3232 12.0128 27.3232H17.1948V31.0919Z"
          fill={T_FILL}
        />
        <path
          d="M28.5009 14.3682C28.5009 14.8886 28.0791 15.3104 27.5588 15.3104H22.8479C22.3275 15.3104 21.9057 15.7323 21.9057 16.2526V26.381C21.9057 26.9014 21.4839 27.3232 20.9635 27.3232H17.1948V11.5417C17.1948 11.0214 17.6166 10.5995 18.137 10.5995H22.8479C23.3682 10.5995 23.79 10.1777 23.79 9.65734V5.65308C23.79 5.13273 23.3682 4.7109 22.8479 4.7109H4.7109V0.942181C4.7109 0.421829 5.13273 0 5.65308 0H27.5588C28.0791 0 28.5009 0.421828 28.5009 0.94218V14.3682Z"
          fill={T_FILL}
        />
      </g>
      <g transform={`translate(${wordmarkOffset} 0)`}>
        <path
          d="M58.0768 10.085C62.4913 10.085 66.5782 13.517 66.5844 18.1967C66.5871 20.222 66.5879 23.6352 66.5881 25.2175C66.5882 25.6854 66.2078 26.0629 65.74 26.0628C65.0727 26.0626 64.1679 26.0631 63.5044 26.0636C63.0388 26.0639 62.6617 25.6869 62.6617 25.2214C62.6617 24.4588 62.6617 23.4667 62.6617 23.4667C62.6617 23.4667 60.8223 26.0641 57.3508 26.0641C53.8793 26.0641 50.0741 23.2487 50.0072 18.1967C49.9404 13.1447 53.6624 10.085 58.0768 10.085ZM58.2443 22.6982C60.4721 22.6982 62.6617 20.8962 62.6617 18.1967C62.6617 15.4972 60.531 13.8635 58.0768 13.8635C55.6227 13.8635 53.7458 15.7426 53.7458 18.1967C53.7458 20.6508 56.0165 22.6982 58.2443 22.6982Z"
          fill={wordmarkFill}
        />
        <path
          d="M93.4885 9.92114C97.9029 9.92114 101.99 13.3532 101.996 18.0329C101.999 20.0582 102 23.4714 102 25.0537C102 25.5216 101.619 25.8991 101.152 25.899C100.484 25.8988 99.5796 25.8993 98.916 25.8998C98.4504 25.9001 98.0733 25.5231 98.0733 25.0576C98.0733 24.295 98.0733 23.3029 98.0733 23.3029C98.0733 23.3029 96.2339 25.9003 92.7624 25.9003C89.2909 25.9003 85.4857 23.0849 85.4189 18.0329C85.352 12.9808 89.074 9.92114 93.4885 9.92114ZM93.6559 22.5344C95.8837 22.5344 98.0733 20.7324 98.0733 18.0329C98.0733 15.3334 95.9426 13.6996 93.4885 13.6996C91.0344 13.6996 89.1574 15.5788 89.1574 18.0329C89.1574 20.487 91.4281 22.5344 93.6559 22.5344Z"
          fill={wordmarkFill}
        />
        <path
          d="M50.6129 6.0918C51.0784 6.0918 51.4566 6.46927 51.4567 6.9348V9.44271C51.4566 9.90819 51.0784 10.2857 50.6129 10.2857H46.3979C45.9323 10.2857 45.5549 10.6631 45.5549 11.1287V24.8815C45.5549 25.3471 45.1774 25.7245 44.7118 25.7245H42.471C42.0055 25.7244 41.628 25.347 41.628 24.8815V11.1287C41.628 10.6631 41.2505 10.2857 40.785 10.2857H36.593C36.1275 10.2857 35.7501 9.90819 35.75 9.44271V6.9348C35.7501 6.46927 36.1275 6.0918 36.593 6.0918H50.6129Z"
          fill={wordmarkFill}
        />
        <path
          d="M72.8696 5.11035C73.3352 5.11035 73.7127 5.48763 73.7127 5.95319V15.3655L76.7651 11.9438L77.892 10.6477C79.152 9.19819 81.3487 9.04474 82.7983 10.3048C82.9069 10.3993 82.9186 10.5641 82.8241 10.6728L77.7162 16.5484L84.4894 25.0355C84.7685 25.3853 84.5194 25.9026 84.0719 25.9026H80.9135C80.8081 25.9026 80.7194 25.8434 80.6447 25.769L75.304 19.3601L73.7127 21.154V24.8821C73.7125 25.3475 73.3351 25.7252 72.8696 25.7252H70.8968C70.4314 25.7252 70.0539 25.3475 70.0537 24.8821V5.95319C70.0537 5.48763 70.4312 5.11035 70.8968 5.11035H72.8696Z"
          fill={wordmarkFill}
        />
      </g>
    </svg>
  )
}
